import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Card, CardBody, CardHeader, CardTitle, Col, Row, Spinner } from "reactstrap"
import { MDBDataTable } from "mdbreact"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient, { API_BASE } from "helpers/apiClient"
import { formatDisplayNumber } from "helpers/indianNumber"

interface BreadcrumbItem {
    title: string
    link: string
}

interface LivePropertiesProps {
    setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

const breadcrumbItems: BreadcrumbItem[] = [
    { title: "Live Properties", link: "#" },
    { title: "Properties List", link: "#" },
]

const GET_LISTINGS_URL = "/api/listings/getListings"

interface ListingRow {
    _id: string
    propertyTitle?: string
    category?: string
    listingCategoryType?: string
    city?: string
    status?: string
    totalSalePrice?: number
    superAreaSqFt?: number
    carpetAreaSqFt?: number
    biddingEnabled?: boolean
    auctionStatus?: "upcoming" | "live" | "ended"
    isUpcoming?: boolean
    auctionEndDate?: string
    propertyImagesUrls?: string[]
}

function listingImageSrc(url?: string): string {
    if (!url) return ""
    const u = String(url).trim()
    if (!u) return ""
    if (u.startsWith("http://") || u.startsWith("https://")) return u
    return `${API_BASE.replace(/\/$/, "")}/${u.replace(/^\//, "")}`
}

function isLiveOrUpcomingListing(l: ListingRow): boolean {
    if (!l.biddingEnabled) return false
    if (l.auctionStatus === "ended") return false
    const end = l.auctionEndDate ? new Date(l.auctionEndDate) : null
    if (end && !Number.isNaN(end.getTime()) && end < new Date()) return false
    return true
}

function isAdminUserRole(role: string | null): boolean {
    const r = (role || "").trim().toLowerCase().replace(/\s+/g, "-").replace(/_/g, "-")
    return r === "admin" || r === "super-admin" || r === "superadmin"
}

const LiveProperties = (props: LivePropertiesProps) => {
    const [listings, setListings] = useState<ListingRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const userRole = useMemo(() => {
        try {
            const raw = localStorage.getItem("authUser")
            if (!raw) return null
            const obj = JSON.parse(raw) as { role?: string }
            return obj?.role ?? null
        } catch {
            return null
        }
    }, [])

    const isAdmin = useMemo(() => isAdminUserRole(userRole), [userRole])

    useEffect(() => {
        props.setBreadcrumbItems("Live Properties", breadcrumbItems)
    }, [props])

    const fetchLive = useCallback(() => {
        setLoading(true)
        setError(null)
        const params = new URLSearchParams()
        params.set("page", "1")
        params.set("limit", "200")
        params.set("biddingEnabled", "true")
        apiClient
            .get(`${GET_LISTINGS_URL}?${params.toString()}`)
            .then((res) => {
                const raw = res?.data?.data
                const list = Array.isArray(raw) ? raw : []
                setListings(list as ListingRow[])
            })
            .catch((err) => {
                setListings([])
                setError(err?.response?.data?.message || err?.message || "Failed to load live properties.")
            })
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        fetchLive()
    }, [fetchLive])

    const liveListings = useMemo(() => listings.filter(isLiveOrUpcomingListing), [listings])

    const data = useMemo(
        () => ({
            columns: [
                { label: "S.No.", field: "id", sort: "asc", width: 50 },
                { label: "Action", field: "action", sort: "disabled", width: 160 },
                { label: "Image", field: "image", sort: "disabled", width: 80 },
                { label: "Title", field: "title", sort: "asc", width: 200 },
                { label: "Type", field: "type", sort: "asc", width: 120 },
                { label: "Sub Type", field: "subType", sort: "asc", width: 140 },
                { label: "City", field: "city", sort: "asc", width: 120 },
                { label: "Price", field: "price", sort: "asc", width: 100 },
                { label: "Area", field: "area", sort: "asc", width: 100 },
                { label: "Status", field: "status", sort: "asc", width: 120 },
            ],
            rows: liveListings.map((item, idx) => {
                const imgUrl = listingImageSrc(item.propertyImagesUrls?.[0])
                const price =
                    item.totalSalePrice != null && !Number.isNaN(Number(item.totalSalePrice))
                        ? `₹${Number(item.totalSalePrice).toLocaleString("en-IN")}`
                        : "—"
                const area =
                    item.superAreaSqFt != null
                        ? `${formatDisplayNumber(item.superAreaSqFt)} sq.ft`
                        : item.carpetAreaSqFt != null
                            ? `${formatDisplayNumber(item.carpetAreaSqFt)} sq.ft`
                            : "—"
                const auc = item.auctionStatus || "—"
                const statusLabel = auc === "—" ? "—" : auc.charAt(0).toUpperCase() + auc.slice(1)

                return {
                    id: String(idx + 1),
                    image: imgUrl ? (
                        <img src={imgUrl} alt="" className="rounded" style={{ width: 48, height: 48, objectFit: "cover" }} />
                    ) : (
                        <span className="text-muted small">—</span>
                    ),
                    title: item.propertyTitle || "—",
                    type: item.category || "—",
                    subType: item.listingCategoryType || "—",
                    city: item.city || "—",
                    price,
                    area,
                    status: statusLabel,
                    action: (
                        <div className="d-flex flex-wrap gap-1">
                            <Link to={`/property/${item._id}`} className="btn btn-sm btn-outline-primary">
                                View
                            </Link>
                            {isAdmin ? (
                                <Link to={`/list-property/${item._id}`} className="btn btn-sm btn-outline-secondary">
                                    Edit
                                </Link>
                            ) : null}
                        </div>
                    ),
                }
            }),
        }),
        [liveListings, isAdmin],
    )

    return (
        <React.Fragment>
            <Row>
                <Col xs="12">
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <CardTitle tag="h5" className="mb-0">
                                Live Properties
                            </CardTitle>
                            <span className="text-muted">
                                {loading ? "Loading…" : `${liveListings.length} live / upcoming`}
                            </span>
                        </CardHeader>
                        <CardBody>
                            {error && (
                                <Alert color="warning" className="mb-3">
                                    {error}
                                </Alert>
                            )}
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                </div>
                            ) : (
                                <MDBDataTable responsive bordered data={data} />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    )
}

export default connect(null, { setBreadcrumbItems })(LiveProperties)
